import type { TFunction } from 'i18next';

const timeoutKey = 'inactive-tab-close-timeout-sec';
const legacyMinutesKey = 'inactive-tab-close-minutes';
const enabledKey = 'inactive-tab-close-enabled';

export const INACTIVE_TAB_CLOSE_CHANGED_EVENT = 'inactive-tab-close-changed';

export const INACTIVE_TAB_CLOSE_OPTIONS = [
  0, 5, 10, 15, 30, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 900,
] as const;

export type InactiveTabCloseTimeoutSec = (typeof INACTIVE_TAB_CLOSE_OPTIONS)[number];

export const notifyInactiveTabCloseChanged = () => {
  window.dispatchEvent(new Event(INACTIVE_TAB_CLOSE_CHANGED_EVENT));
};

export const setInactiveTabCloseEnabledLocalStorage = (isEnabled: boolean) => {
  localStorage.setItem(enabledKey, String(isEnabled));
  notifyInactiveTabCloseChanged();
};

export const getInactiveTabCloseEnabledLocalStorage = () => {
  return localStorage.getItem(enabledKey) === 'true';
};

const parseInactiveTabCloseTimeoutSec = (value: number): InactiveTabCloseTimeoutSec => {
  return INACTIVE_TAB_CLOSE_OPTIONS.includes(value as InactiveTabCloseTimeoutSec)
    ? (value as InactiveTabCloseTimeoutSec)
    : 0;
};

export const getInactiveTabCloseLocalStorage = (): InactiveTabCloseTimeoutSec => {
  const storedSec = localStorage.getItem(timeoutKey);
  if (storedSec !== null) {
    return parseInactiveTabCloseTimeoutSec(Number(storedSec));
  }

  const legacyMinutes = Number(localStorage.getItem(legacyMinutesKey) || 0);
  if (legacyMinutes <= 0) {
    return 0;
  }

  return parseInactiveTabCloseTimeoutSec(legacyMinutes * 60);
};

export const setInactiveTabCloseLocalStorage = (timeoutSec: InactiveTabCloseTimeoutSec) => {
  localStorage.setItem(timeoutKey, String(timeoutSec));
  notifyInactiveTabCloseChanged();
};

export const formatInactiveTabCloseOptionLabel = (
  timeoutSec: InactiveTabCloseTimeoutSec,
  t: TFunction<'translation'>,
) => {
  if (timeoutSec === 0) {
    return t('inactive_tab_close_never');
  }

  if (timeoutSec < 60) {
    return t('inactive_tab_close_seconds', { seconds: timeoutSec });
  }

  return t('inactive_tab_close_minutes', { minutes: timeoutSec / 60 });
};

export const isInactiveTabCloseActive = () => {
  return getInactiveTabCloseEnabledLocalStorage() && getInactiveTabCloseLocalStorage() > 0;
};
